import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { Button } from './ui/Button';

/**
 * ErrorBoundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the app.
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * Or with custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // TODO: Log to error reporting service (Sentry, LogRocket, etc.)
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-alt px-4">
          <div className="max-w-md w-full">
            <div className="bg-white shadow-lg rounded-lg p-6 text-center">
              {/* Error Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon
                    icon="lucide:alert-triangle"
                    className="w-8 h-8 text-red-600"
                  />
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-2xl font-bold text-default mb-2">
                Algo salió mal
              </h1>

              {/* Error Description */}
              <p className="text-subtle mb-6">
                Se ha producido un error inesperado. Puedes intentar recargar la
                página o volver a intentarlo más tarde.
              </p>

              {/* Error Details (Development Only) */}
              {process.env.NODE_ENV !== 'production' && error && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-muted hover:text-default mb-2">
                    Detalles técnicos (solo desarrollo)
                  </summary>
                  <div className="bg-surface-alt rounded-lg p-4 text-xs font-mono overflow-auto max-h-48">
                    <div className="text-red-600 font-bold mb-2">
                      {error.toString()}
                    </div>
                    {errorInfo && (
                      <div className="text-muted whitespace-pre-wrap">
                        {errorInfo.componentStack}
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={this.handleReset}
                  variant="secondary"
                  className="flex-1 sm:flex-none"
                >
                  <Icon icon="lucide:refresh-cw" className="w-4 h-4 mr-2" />
                  Intentar de nuevo
                </Button>
                <Button
                  onClick={this.handleReload}
                  variant="primary"
                  className="flex-1 sm:flex-none"
                >
                  <Icon icon="lucide:rotate-cw" className="w-4 h-4 mr-2" />
                  Recargar página
                </Button>
              </div>

              {/* Support Link (Optional) */}
              <div className="mt-6 pt-6 border-t border-border-default">
                <p className="text-sm text-muted">
                  Si el problema persiste, contacta con{' '}
                  <a
                    href="mailto:support@dolmahr.com"
                    className="text-brand hover:text-brand font-medium"
                  >
                    soporte técnico
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
};
